













import {Component, Mixins, Prop} from 'vue-property-decorator'
import ECharts from 'vue-echarts/components/ECharts.vue'
import 'echarts/lib/chart/line'
import 'echarts/lib/component/tooltip'
import moment, {Moment} from 'moment'
import _ from 'lodash'
import {MixinScreenSize} from '@/components/mixins/MixinScreenSize'
import {GithubOrganizationCollection} from '@/model/collection/GithubOrganizationCollection'
import {GithubRepositoryCollection} from '@/model/collection/GithubRepositoryCollection'
import {GithubRepositoryCommitCollection} from '@/model/collection/GithubRepositoryCommitCollection'

type Serie = {
  name: string
  type: 'line'
  stack: string
  lineStyle: {width: number}
  itemStyle: {color: string}
  showSymbol: boolean
  emphasis: {itemStyle: {color: string}}
  areaStyle: Record<string, any>
  data: number[]
}

@Component({
  components: {ECharts},
})
export default class CDActivityVolumeChart extends Mixins(MixinScreenSize) {
  @Prop({type: GithubOrganizationCollection, required: true})
  organizationCollection!: GithubOrganizationCollection

  @Prop({type: Object, default: () => ({})})
  commitsByOrganization!: Record<number, number>

  @Prop({type: GithubRepositoryCollection, required: true})
  repositoryCollection!: GithubRepositoryCollection

  @Prop({type: GithubRepositoryCommitCollection, required: true})
  repositoryCommitCollection!: GithubRepositoryCommitCollection

  @Prop({type: Number, required: true}) metric!: number

  @Prop({type: Number, required: true}) periodDays!: number

  @Prop({type: Number, default: null}) selectedOrganizationId!: number | null

  @Prop({type: Array, default: () => []}) visibleItemsIds!: number[]

  get xAxisData(): Moment[] {
    const initialDate = moment().subtract(this.periodDays, 'days')
    return Array.from({length: this.periodDays}).map(() => {
      const nextDate = initialDate.add(1, 'day')
      return _.cloneDeep(nextDate)
    })
  }

  readonly REPO_COLORS = [
    '#4CAF50',
    '#2196F3',
    '#FF9800',
    '#9C27B0',
    '#EF5350',
    '#00BCD4',
    '#FFEB3B',
    '#795548',
    '#607D8B',
    '#EC407A',
  ]

  get visibleSet(): Set<number> {
    return new Set(this.visibleItemsIds)
  }

  get selectedOrganizations() {
    const filtered = this.organizationCollection.items.filter(it =>
      this.visibleSet.has(it.$id)
    )
    return [...filtered].sort((a, b) => {
      const ca = this.commitsByOrganization[a.$id] ?? 0
      const cb = this.commitsByOrganization[b.$id] ?? 0
      if (cb !== ca) return cb - ca
      return (a.name || a.$tag || '').localeCompare(b.name || b.$tag || '')
    })
  }

  get repoIdsByOrgId() {
    const map = new Map<number, Set<number>>()
    this.repositoryCollection.items.forEach(repo => {
      const orgId = repo.githubOrganization?.$id
      const repoId = repo.$id
      if (!orgId || !repoId) return
      if (!map.has(orgId)) map.set(orgId, new Set())
      map.get(orgId)!.add(repoId)
    })
    return map
  }

  buildCommitsSeriesData(orgId: number): number[] {
    const repoIds = this.repoIdsByOrgId.get(orgId) || new Set<number>()
    const counts = new Map<string, number>()
    this.repositoryCommitCollection.items.forEach(commit => {
      const repoId = commit.githubRepository?.$id
      if (!repoId || !repoIds.has(repoId)) return
      const day = moment(commit.committedAt).format('YYYY-MM-DD')
      counts.set(day, (counts.get(day) || 0) + 1)
    })
    return this.xAxisData.map(d => counts.get(d.format('YYYY-MM-DD')) || 0)
  }

  buildCommitsSeriesDataForRepo(repoId: number): number[] {
    const counts = new Map<string, number>()
    this.repositoryCommitCollection.items.forEach(commit => {
      if (commit.githubRepository?.$id !== repoId) return
      const day = moment(commit.committedAt).format('YYYY-MM-DD')
      counts.set(day, (counts.get(day) || 0) + 1)
    })
    return this.xAxisData.map(d => counts.get(d.format('YYYY-MM-DD')) || 0)
  }

  get orgSeries(): Serie[] {
    return this.selectedOrganizations
      .map(org => {
        const data = this.buildCommitsSeriesData(org.$id)
        const total = data.reduce((a, b) => a + b, 0)
        const color = org.colorHex ? `#${org.colorHex}` : '#4b5563'
        return {
          key: org.$tag,
          total,
          allTime: this.commitsByOrganization[org.$id] ?? 0,
          serie: {
            name: org.$tag,
            type: 'line' as const,
            stack: 'activity',
            lineStyle: {width: 0},
            itemStyle: {color},
            showSymbol: false,
            emphasis: {itemStyle: {color}},
            areaStyle: {},
            data,
          } as Serie,
        }
      })
      .sort((a, b) => {
        if (b.allTime !== a.allTime) return b.allTime - a.allTime
        if (b.total !== a.total) return b.total - a.total
        return a.key.toLowerCase().localeCompare(b.key.toLowerCase())
      })
      .map(it => it.serie)
  }

  get repoSeries(): Serie[] {
    return [...this.repositoryCollection.items]
      .sort((a, b) => {
        const ca = a.commitsCount ?? 0
        const cb = b.commitsCount ?? 0
        if (cb !== ca) return cb - ca
        return (a.name || '').localeCompare(b.name || '')
      })
      .slice(0, 10)
      .filter(repo => this.visibleSet.has(repo.$id))
      .map((repo, index) => {
        const data = this.buildCommitsSeriesDataForRepo(repo.$id)
        const color = repo.colorHex
          ? `#${repo.colorHex}`
          : this.REPO_COLORS[index % this.REPO_COLORS.length]
        return {
          name: repo.name ?? String(repo.$id),
          type: 'line' as const,
          stack: 'activity',
          lineStyle: {width: 0},
          itemStyle: {color},
          showSymbol: false,
          emphasis: {itemStyle: {color}},
          areaStyle: {},
          data,
        } as Serie
      })
  }

  get series(): Serie[] {
    return this.selectedOrganizationId !== null
      ? this.repoSeries
      : this.orgSeries
  }

  get options() {
    return {
      tooltip: {
        trigger: 'axis',
        confine: true,
        formatter: (params: any) => {
          const raw = Array.isArray(params) ? params.slice() : [params]
          const items = raw.filter((it: any) => {
            const value =
              Number(Array.isArray(it.value) ? it.value[1] : it.value) || 0
            return value > 0
          })

          const header = raw[0]?.axisValueLabel ?? raw[0]?.name ?? ''
          if (!items.length) {
            return header
          }

          items.sort((a: any, b: any) => {
            const aVal =
              Number(Array.isArray(a.value) ? a.value[1] : a.value) || 0
            const bVal =
              Number(Array.isArray(b.value) ? b.value[1] : b.value) || 0
            if (bVal !== aVal) return bVal - aVal
            return String(a.seriesName || '').localeCompare(
              String(b.seriesName || '')
            )
          })

          const lines = items.map((it: any) => {
            const value =
              Number(Array.isArray(it.value) ? it.value[1] : it.value) || 0
            const marker = it.marker ?? ''
            const name = it.seriesName ?? ''
            return `${marker}${name}: <b>${value}</b>`
          })

          return [header, ...lines].join('<br/>')
        },
      },
      legend: {
        data: this.series.map(s => s.name),
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: this.xAxisData.map(d => d.format('M/D')),
        axisTick: {show: false},
        axisLabel: {
          interval: this.periodDays > 20 ? (this.isDesktop ? 3 : 6) : 0,
          margin: 12,
          textStyle: {fontWeight: '800', color: '#82919c'},
        },
      },
      yAxis: {
        type: 'value',
        axisLine: {show: false},
        axisTick: {show: false},
        splitLine: {lineStyle: {type: 'dashed'}},
        axisLabel: {
          textStyle: {fontWeight: '800', color: '#82919c'},
          formatter: (value: any) => {
            if (value == 0) return ''
            if (value >= 1000) return `${value / 1000}k`
            return value
          },
        },
      },
      grid: {
        top: '6%',
        left: '2%',
        right: '3%',
        bottom: '6%',
        containLabel: true,
      },
      series: this.series,
    }
  }
}
